import { Component, inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface PlayerColorPickerData {
  playerNumber: 1 | 2;
  currentColor: string;
  isDarkTheme?: boolean;
}

@Component({
  selector: 'app-player-color-picker-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './player-color-picker-dialog.component.html',
  styleUrls: ['./player-color-picker-dialog.component.scss']
})
export class PlayerColorPickerDialogComponent implements AfterViewInit {
  private dialogRef = inject(MatDialogRef<PlayerColorPickerDialogComponent>);
  data = inject<PlayerColorPickerData>(MAT_DIALOG_DATA);

  @ViewChild('saturationCanvas') saturationCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('hueCanvas') hueCanvas!: ElementRef<HTMLCanvasElement>;

  currentHue = 0; // Matiz actual (0-360)

  // Colores predefinidos comunes en ropa deportiva
  presetColors = [
    { hex: '#FF8C42', name: 'Naranja' },      // Naranja (Bolar)
    { hex: '#4AA3FF', name: 'Azul claro' },   // Azul claro
    { hex: '#FF6B9D', name: 'Rosa' },         // Rosa
    { hex: '#5EEAD4', name: 'Verde menta' },  // Turquesa/Verde menta
    { hex: '#A855F7', name: 'Morado' },       // Morado
    { hex: '#6B7280', name: 'Gris' },         // Gris
    { hex: '#FFFFFF', name: 'Blanco' },       // Blanco
    { hex: '#FCD34D', name: 'Amarillo' },     // Amarillo
    { hex: '#10B981', name: 'Verde' },        // Verde
    { hex: '#1E40AF', name: 'Azul marino' },  // Azul marino
    { hex: '#1F2937', name: 'Negro' }         // Negro
  ];

  selectedColor: string = this.data.currentColor;
  showCustomPicker = false;

  ngAfterViewInit(): void {
    if (this.showCustomPicker) {
      this.drawHueSlider();
      this.drawSaturationCanvas();
    }
  }

  selectColor(color: string): void {
    this.selectedColor = color;
    // Cerrar automáticamente al seleccionar un color preset
    this.confirm();
  }

  openCustomPicker(): void {
    this.showCustomPicker = true;
    // Esperar un tick para que los canvas se renderizen
    setTimeout(() => {
      this.drawHueSlider();
      this.drawSaturationCanvas();
    }, 0);
  }

  drawHueSlider(): void {
    const canvas = this.hueCanvas?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);

    // Crear gradiente de arcoíris (todos los matices)
    gradient.addColorStop(0, '#ff0000');     // Rojo
    gradient.addColorStop(0.17, '#ffff00');  // Amarillo
    gradient.addColorStop(0.33, '#00ff00');  // Verde
    gradient.addColorStop(0.50, '#00ffff');  // Cyan
    gradient.addColorStop(0.67, '#0000ff');  // Azul
    gradient.addColorStop(0.83, '#ff00ff');  // Magenta
    gradient.addColorStop(1, '#ff0000');     // Rojo

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  drawSaturationCanvas(): void {
    const canvas = this.saturationCanvas?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Color base (matiz puro)
    const baseColor = this.hsvToHex(this.currentHue, 1, 1);
    ctx.fillStyle = baseColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Gradiente horizontal: blanco a transparente (saturación)
    const saturationGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    saturationGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    saturationGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = saturationGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Gradiente vertical: transparente a negro (brillo)
    const brightnessGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    brightnessGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    brightnessGradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
    ctx.fillStyle = brightnessGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  onHueClick(event: MouseEvent): void {
    this.pickHueFromEvent(event);
  }

  onHueTouch(event: TouchEvent): void {
    event.preventDefault();
    if (event.touches.length > 0) {
      this.pickHueFromEvent(event.touches[0]);
    }
  }

  private pickHueFromEvent(event: MouseEvent | Touch): void {
    const canvas = this.hueCanvas?.nativeElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(event.clientX - rect.left, canvas.width));

    this.currentHue = (x / canvas.width) * 360;
    this.drawSaturationCanvas();

    // Actualizar color seleccionado manteniendo saturación y brillo actuales
    this.updateColorFromCurrentHSV();
  }

  onSaturationClick(event: MouseEvent): void {
    this.pickSaturationFromEvent(event);
  }

  onSaturationTouch(event: TouchEvent): void {
    event.preventDefault();
    if (event.touches.length > 0) {
      this.pickSaturationFromEvent(event.touches[0]);
    }
  }

  private pickSaturationFromEvent(event: MouseEvent | Touch): void {
    const canvas = this.saturationCanvas?.nativeElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(event.clientX - rect.left, canvas.width));
    const y = Math.max(0, Math.min(event.clientY - rect.top, canvas.height));

    const saturation = x / canvas.width;        // Izquierda = blanco (0), Derecha = saturado (1)
    const value = 1 - (y / canvas.height);      // Arriba = brillante (1), Abajo = oscuro (0)

    this.selectedColor = this.hsvToHex(this.currentHue, saturation, value);
  }

  private updateColorFromCurrentHSV(): void {
    // Mantener la saturación y brillo actuales cuando cambia el hue
    const saturation = 1;
    const value = 1;
    this.selectedColor = this.hsvToHex(this.currentHue, saturation, value);
  }

  private hsvToHex(h: number, s: number, v: number): string {
    const rgb = this.hsvToRgb(h, s, v);
    return `#${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}`;
  }

  private hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;

    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else {
      r = c; g = 0; b = x;
    }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255)
    };
  }

  confirmCustomColor(): void {
    this.confirm();
  }

  cancelCustomPicker(): void {
    this.showCustomPicker = false;
    this.selectedColor = this.data.currentColor; // Restaurar color original
  }

  confirm(): void {
    this.dialogRef.close({ color: this.selectedColor });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
